import React, { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { identifyUser, posthog } from "@/lib/posthog";

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isPremium: boolean;
  onboarded: boolean | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  markOnboarded: () => Promise<void>;
};

// Site domain for redirects - use the actual deployed domain
const SITE_URL = import.meta.env.VITE_APP_URL;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [onboarded, setOnboarded] = useState<boolean | null>(null);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();

  // Function to check profile status (premium + onboarding)
  const checkProfileStatus = async (userId: string) => {
    try {
      console.log("Checking profile status...");
      const { data, error } = await supabase
        .from('profiles')
        .select('is_premium, onboarded')
        .eq('id', userId)
        .single();
      
      if (error) {
        // If the profile doesn't exist, create it
        if (error.code === 'PGRST116') {
          console.log("Profile not found, creating new profile");
          const { error: createError } = await supabase
            .from('profiles')
            .insert({
              id: userId,
              is_premium: false,
              onboarded: false,
            });
          
          if (createError) {
            console.error("Error creating profile:", createError);
            throw createError;
          }
          
          console.log("Profile created successfully");
          setIsPremium(false);
          setOnboarded(false);
          return;
        }
        
        throw error;
      }
      
      console.log("Got profile status:", { is_premium: data?.is_premium, onboarded: data?.onboarded });
      setIsPremium(data?.is_premium || false);
      setOnboarded(Boolean(data?.onboarded));
    } catch (error) {
      console.error("Error checking profile status:", error);
      setIsPremium(false);
      setOnboarded(null);
    }
  };

  const markOnboarded = async () => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ onboarded: true })
        .eq('id', user.id);
      if (error) throw error;
      setOnboarded(true);
    } catch (error) {
      console.error("Error marking onboarded:", error);
      throw error;
    }
  };

  // Initialize auth state
  useEffect(() => {
    let mounted = true;
    console.log("Auth effect starting, setting up listeners");

    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log("Auth state changed:", event);
        console.log("Mounted status:", mounted);
        
        if (!mounted) {
          console.log("Component not mounted, skipping auth state update");
          return;
        }
        
        // Handle sign out first
        if (event === 'SIGNED_OUT' || (!currentSession && user)) {
          console.log("User signed out or session expired, clearing state");
          setSession(null);
          setUser(null);
          setIsPremium(false);
          setOnboarded(null);
          setLoading(false);
          setInitialized(true);
          // Reset PostHog on sign out
          posthog.reset();
          return;
        }
        
        // Handle other auth state changes
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (currentSession?.user) {
          // Identify user in PostHog
          identifyUser(currentSession.user.id, {
            email: currentSession.user.email,
            created_at: currentSession.user.created_at,
          });
          // Don't await this, let it run in background
          checkProfileStatus(currentSession.user.id).then(() => {
            if (mounted) {
              console.log("Profile status updated after auth state change");
            }
          });
        } else {
          setIsPremium(false);
          setOnboarded(null);
        }
        
        if (event === 'PASSWORD_RECOVERY') {
          toast.info('Password recovery initiated');
          navigate('/profile');
        }
        
        setLoading(false);
        setInitialized(true);
        console.log("Auth state update completed");
      }
    );

    // Then initialize
    const initialize = async () => {
      try {
        console.log("Starting auth initialization");
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        console.log("Initial session found:", !!initialSession?.user);
        
        if (!mounted) {
          console.log("Component unmounted during initialization");
          return;
        }
        
        if (initialSession?.user) {
          console.log("Setting initial session and user");
          setSession(initialSession);
          setUser(initialSession.user);
          
          // Identify user in PostHog
          identifyUser(initialSession.user.id, {
            email: initialSession.user.email,
            created_at: initialSession.user.created_at,
          });
          
          // Don't await profile status check
          checkProfileStatus(initialSession.user.id).then(() => {
            if (mounted) {
              console.log("Profile status check completed after initialization");
            }
          });
        } else {
          console.log("No initial session found");
        }
        
        // Always set these states
        if (mounted) {
          setInitialized(true);
          setLoading(false);
          console.log("Initialization completed");
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        if (mounted) {
          setInitialized(true);
          setLoading(false);
          setIsPremium(false);
          setOnboarded(null);
        }
      }
    };

    initialize();

    return () => {
      console.log("Cleaning up auth effect");
      mounted = false;
      subscription.unsubscribe();
    };
  }, []); // Remove navigate from dependencies

  // Effect to manage user timezone
  useEffect(() => {
    const setTimezoneIfNeeded = async (userId: string) => {
      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('timezone')
          .eq('id', userId)
          .single();

        if (profileError && profileError.code !== 'PGRST116') { // Ignore "row not found"
          throw profileError;
        }

        if (!profile || !profile.timezone) {
          const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ timezone: browserTimezone })
            .eq('id', userId);
          
          if (updateError) throw updateError;
          console.log(`User timezone set to: ${browserTimezone}`);
        }
      } catch (error) {
        console.error("Error managing user timezone:", error);
      }
    };

    if (user) {
      setTimezoneIfNeeded(user.id);
    }
  }, [user]);

  // Add a debug effect to monitor state changes
  useEffect(() => {
    console.log("State updated:", { initialized, loading, hasUser: !!user, onboarded });
  }, [initialized, loading, user, onboarded]);

  // Separate effect for navigation
  useEffect(() => {
    const publicRoutes = ['/', '/auth', '/resend-activation', '/privacy', '/terms', '/pricing', '/blog', '/pomodoro'];
    if (!user && !loading && initialized) {
      const currentPath = window.location.pathname;
      // Check if current path starts with any public route (for blog posts like /blog/slug)
      const isPublicRoute = publicRoutes.some(route => 
        currentPath === route || currentPath.startsWith(route + '/')
      );
      if (!isPublicRoute) {
        console.log("No user after initialization, redirecting to auth");
        navigate('/auth');
      }
    }
  }, [user, loading, initialized, navigate]);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      console.log("Attempting sign in...");
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      
      console.log("Sign in successful");
      
      // Remove the artificial delay and let the auth state listener handle navigation
      if (data.user) {
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error("Sign in error:", error);
      toast.error(error.message || 'Error signing in');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setLoading(true);
      console.log("Attempting sign up...");
      // Sign up the user
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${SITE_URL}/auth`
        }
      });
      
      if (signUpError) throw signUpError;
      
      console.log("Sign up successful");
      
      // Create a profile record for the user
      if (authData.user) {
        console.log("Creating profile for new user");
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: authData.user.id,
            is_premium: false,
            onboarded: false,
          });
        
        if (profileError) {
          console.error("Error creating profile:", profileError);
          // Don't throw here, as the user is already created
          toast.error("Account created but there was an error setting up your profile. Please contact support.");
        } else {
          console.log("Profile created successfully");
        }
      }
    } catch (error: any) {
      console.error("Sign up error:", error);
      toast.error(error.message || 'Error signing up');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      console.log("Signing out");
      
      // First clear the local state
      setUser(null);
      setSession(null);
      setIsPremium(false);
      setOnboarded(null);
      
      try {
        // Try to sign out from Supabase, but don't throw if it fails
        await supabase.auth.signOut();
      } catch (error: any) {
        // Log the error but continue with local sign out
        console.warn("Supabase sign out error:", error);
      }
      
      // Force clear the Supabase session
      await supabase.auth.setSession({
        access_token: "",
        refresh_token: "",
      });
      
      // Navigate after state is cleared
      navigate('/', { replace: true });
      
      // Show success message after navigation
      toast.success('Signed out successfully');
    } catch (error: any) {
      console.error("Sign out error:", error);
      // Don't show error to user since we've already cleared local state
    } finally {
      setLoading(false);
    }
  };

  // Don't render until we've initialized auth
  if (!initialized) {
    console.log("Waiting for initialization...");
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        isPremium,
        onboarded,
        signIn,
        signUp,
        signOut,
        markOnboarded,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
